const destroyZoom = () => document.dispatchEvent(new CustomEvent('destroyZoom'));
import { buildProductNodes, finalSaleCalculate } from '../../utils/finalSale';
import { fetchKlarnaData } from '../../utils/klarnaConfig';
import ConstructorTracking from '../ConstructorTracking';
import { Gallery } from '../../components/Gallery';
import { getSectionInnerHTML } from '../../sections/PDP/utils';

// Set current variant barcode
const replaceVariantBarcode = (variant) => {
  const currentVariantElement =
    document.querySelector('[data-current-variant-barcode]') || document.createElement('div');
  currentVariantElement.setAttribute('data-current-variant-barcode', variant.barcode || variant.sku);
  if (!currentVariantElement.parentElement) {
    currentVariantElement.style.display = 'none';
    document.body.appendChild(currentVariantElement);
  }

  // Set the barcode on the ProductOptions container for easier access
  const productOptionsContainer = document.querySelector('.ProductOptions');
  if (productOptionsContainer) {
    productOptionsContainer.setAttribute('data-variant-barcode', variant.barcode || variant.sku);
  }
};

// update the style number under the title
const updateStyleNumber = (variant) => {
  const styleNumberValue = document.querySelector('[data-style-number-value]');
  if (styleNumberValue) {
    styleNumberValue.innerHTML = variant.barcode;
  }
};

// update size under the title
const updateSizeValue = (variant) => {
  const sizeVariantValue = document.querySelector('[data-size-variant-value]');
  const shoeSize = sizeVariantValue.getAttribute('data-size-shoe');
  if (sizeVariantValue) {
    sizeVariantValue.innerHTML = `${shoeSize == 'true' ? 'US ' : ''}${variant.option2}`;
  }
};

// update the color name
const updateColorValue = (variant) => {
  const colorVariantValue = document.querySelector('[data-color-variant-value]');
  if (colorVariantValue) {
    colorVariantValue.innerHTML = variant.option1;
  }
};

// Update low inventory message
const updateLowInvetoryMessage = (variant, inventory_json) => {
  const inventory = Number(inventory_json[variant.id]);
  const lowInventoryMessage = document.querySelector('[data-low-inventory-message]');
  if (lowInventoryMessage) {
    if (inventory < 10 && inventory >= 1) {
      lowInventoryMessage.textContent = 'Almost Gone';
      lowInventoryMessage.style.display = 'block';
    } else {
      lowInventoryMessage.style.display = 'none';
    }
  }
};

// Update Klarna widget value with a new price
const updateKlarnaPaymentValue = (variant) => {
  // Exit successfully for international user experiences
  if (window.theme?.country_iso !== 'US') return;

  const klarnaWrapper = document.querySelector('[data-klarna-osm]');
  if (!klarnaWrapper) return;

  // Get the parent wrapper
  const bnplWrapper = klarnaWrapper.closest('.pdp-c-atc-bnpl');

  // Hide the wrapper while loading
  if (bnplWrapper) {
    bnplWrapper.style.visibility = 'hidden';
  }

  // Get currency and price for Klarna key
  const currency = window.theme?.currency?.toLowerCase() || 'usd';
  const priceInCents = variant?.price || 0;
  const klarnaKey = `price_${currency}_${priceInCents}`;

  // Update the key attribute
  klarnaWrapper.setAttribute('data-klarna-key', klarnaKey);

  // Fetch Klarna data and populate
  fetchKlarnaData(klarnaKey)
    .then((data) => {
      if (data) {
        // Extract TEXT_MAIN and remove 'Klarna' from it
        let textMain = data.TEXT_MAIN || '';
        if (textMain) {
          textMain = textMain.replace(/Klarna/gi, '').trim();
          klarnaWrapper.setAttribute('data-klarna-text-main', textMain);
        }

        // Extract ACTION_LEARN_MORE (the URL)
        if (data.ACTION_LEARN_MORE) {
          klarnaWrapper.setAttribute('data-klarna-learn-more', 'Learn More');
          klarnaWrapper.setAttribute('data-klarna-learn-url', data.ACTION_LEARN_MORE);
        }

        // Update UI
        const textEl = klarnaWrapper.querySelector('[data-klarna-text-ui]');
        if (textEl) textEl.textContent = textMain;

        // Only show if we have all the necessary data (text and URL)
        if (textMain && data.ACTION_LEARN_MORE && bnplWrapper) {
          // Use a small delay to ensure smooth rendering
          requestAnimationFrame(() => {
            bnplWrapper.style.visibility = 'visible';
          });
        } else if (bnplWrapper) {
          bnplWrapper.style.visibility = 'hidden';
        }
      } else {
        // No data received, keep hidden
        if (bnplWrapper) {
          bnplWrapper.style.visibility = 'hidden';
        }
      }
    })
    .catch((err) => {
      // On error, keep hidden
      console.warn('[Klarna] Failed to update payment value:', err);
      if (bnplWrapper) {
        bnplWrapper.style.visibility = 'hidden';
      }
    });
};

// Turn off/on the clearance bagde
const switchClearanceBadge = (variant) => {
  const badgeClearance = document.querySelector('[data-badge-clearance]');
  if (badgeClearance) {
    if (variant.price >= variant.compare_at_price) {
      badgeClearance.style.display = 'none';
    } else {
      badgeClearance.style.display = 'block';
    }
  }
};

// Update SKU and Variant ID
const replaceSkuAndVariantId = (variant) => {
  const skuAttribute = document.querySelector('[data-sku]');
  if (skuAttribute) {
    skuAttribute.setAttribute('data-sku', variant.sku);
  }
  const variantIdAttribute = document.querySelector('[data-variant-id]');
  if (variantIdAttribute) {
    variantIdAttribute.setAttribute('data-variant-id', variant.id);
  }
};

// Turn off/on the final sale message
const toggleFinalSaleMessage = (variant, product_json) => {
  const variantFinalSaleMetafieldJson = JSON.parse(
    document.querySelector('[data-variant-scoped-final-sale-metafield-json]').innerHTML,
  );
  const productNodes = buildProductNodes([variant], product_json, variantFinalSaleMetafieldJson);

  finalSaleCalculate({
    isInternational: window.theme.country_iso !== 'US',
    productNodes,
  })
    .then((result) => {
      const variantResult = result.at(0);

      const showForClearance = document.querySelector('[data-show-for-clearance]');
      if (variantResult.finalSale) {
        showForClearance.style.display = 'block';
      } else {
        showForClearance.style.display = 'none';
      }
    })
    .catch((err) => {
      console.error(`Error while trying calculate final sale on pdp or quick shop: ${err}`);
    });
};

// Update sticky nav options and select values
// TODO: This might be removed in the future, it is no londer used
const syncStickyNavOptions = (variant) => {
  const container = document.querySelector('body'); // Adjust this if necessary to point to the correct container element
  const select1 = container.querySelector('[data-custom-option-selector="option1"] select');
  const select2 = container.querySelector('[data-custom-option-selector="option2"] select');

  if (select1) {
    select1.value = variant.option1;
  }

  if (select2) {
    select2.value = variant.option2;
  }
};

// Sync the sticky secondary nav options
// TODO: This might be removed in the future, it is no londer used
const syncStickySecondaryNavOptions = (variant) => {
  document
    .querySelectorAll('[data-secondary-color-variant-selector]')
    .forEach((el) => el.classList.remove('ProductOption__variant--selected'));
  document
    .querySelectorAll('[data-secondary-size-variant-selector]')
    .forEach((el) => el.classList.remove('ProductOption__variant--selected'));

  const secondaryColorSelector = document.querySelector(
    `[data-secondary-color-variant-selector][data-value="${variant.option1}"]`,
  );
  if (secondaryColorSelector) secondaryColorSelector.classList.add('ProductOption__variant--selected');

  const secondarySizeSelector = document.querySelector(
    `[data-secondary-size-variant-selector][data-value="${variant.option2}"]`,
  );
  if (secondarySizeSelector) secondarySizeSelector.classList.add('ProductOption__variant--selected');
};

// Sync sold out items
const syncSoldOutItemsMobileSticky = () => {
  document.querySelectorAll('[data-secondary-size-variant-selector]').forEach((el) => {
    el.classList.remove('ProductOption__variant--unavailable');
    el.classList.remove('ProductOption__variant--sold-out');
  });

  document.querySelectorAll('.ProductOption__variant--unavailable[data-size-variant-selector]').forEach((el) => {
    const val = el.getAttribute('data-value');
    const target = document.querySelector(`[data-secondary-size-variant-selector][data-value="${val}"]`);
    if (target) target.classList.add('ProductOption__variant--unavailable');
  });

  document.querySelectorAll('.ProductOption__variant--sold-out[data-size-variant-selector]').forEach((el) => {
    const val = el.getAttribute('data-value');
    const target = document.querySelector(`[data-secondary-size-variant-selector][data-value="${val}"]`);
    if (target) target.classList.add('ProductOption__variant--sold-out');
  });
};

/**
 * Re-fetch the QuickView gallery section for a new variant and re-initialize Gallery
 * Uses the same server-side section rendering pattern as PDP/colorSelector.js
 */
const refetchQuickViewGallery = (variant, product_json) => {
  const galleryEl = document.querySelector('#QuickView.Modal--open #quickview-gallery');
  if (!galleryEl) return;

  const productPath = `/products/${product_json.handle}`;
  const sectionUrl = `${productPath}?variant=${variant.id}&sections=quickview-gallery-content`;

  fetch(sectionUrl)
    .then((response) => {
      if (!response.ok) throw new Error('QuickView gallery re-fetch failed');
      return response.json();
    })
    .then((data) => {
      const newHtml = getSectionInnerHTML(data['quickview-gallery-content'], '.shopify-section');
      if (newHtml) {
        galleryEl.innerHTML = newHtml;
        const galleryInstance = new Gallery({
          gallery: galleryEl,
          prefixClassName: 'pdp-c-gallery',
          features: { keydown: false, zoom: false },
        });
        galleryInstance.setObservers();
        setTimeout(() => {
          galleryInstance.init();
          if (galleryInstance.carousel) {
            galleryInstance.carousel.scrollTo({ left: 0 });
          }
        }, 0);
      }
    })
    .catch((error) => {
      console.error('QuickView gallery re-fetch error:', error);
    });
};

/**
 * It update/refresh images, thumbnails images, carousel, and swipe images and non user-visible values
 * each time customer swicthes between size and color selectors
 * @param {Object} variant - variant data
 * @param {Object} inventory_json - availability data
 * @param {Object} product_json - product data
 * @param {Boolean} firstTimeLoading - determine if it was reloaded or not
 * @param {String} option - it will store what customer selected either 'color' or 'size', it will internally address the rest of actions
 * @returns {void} - it'll return no data
 */
const updateSelectedVariant = (variant, inventory_json, product_json, firstTimeLoading, option) => {
  if (!variant) {
    return;
  }

  const quickViewModal = document.querySelector('#QuickView.Modal--open');
  const isBuiltWithFastSimon = quickViewModal?.getAttribute('data-built-with-fastsimon') === 'true';

  if (isBuiltWithFastSimon) {
    return;
  }

  // Store Constructor Tracking instance
  let cnstrcTracking;
  // it checks if Constructor Tracking instance already exists to not create a new one each time
  if (window.cnsQuickView && window.cnsQuickView.validateObj(quickViewModal)) {
    cnstrcTracking = window.cnsQuickView;
  } else {
    const cta = quickViewModal.querySelector('button[type="submit"]');
    cnstrcTracking = new ConstructorTracking({
      container: quickViewModal,
      isValidCta: cta?.getAttribute('data-add-to-cart') ? true : false,
    });
    window.cnsQuickView = cnstrcTracking;
  }

  if (!firstTimeLoading && option !== 'size') {
    destroyZoom();

    updateColorValue(variant);

    const variantChangeEvent = new CustomEvent('variantChange', {
      detail: {
        variant: variant,
      },
    });
    document.dispatchEvent(variantChangeEvent);

    refetchQuickViewGallery(variant, product_json);
  }

  // update size under the title
  option === 'size' && updateSizeValue(variant);

  // they aren't user-visible tasks. So they can be deferred
  setTimeout(() => {
    // Assign the current variant barcode
    replaceVariantBarcode(variant);

    // update the style number under the title
    updateStyleNumber(variant);

    // update low inventory message
    updateLowInvetoryMessage(variant, inventory_json);

    // Update Klarna widget value on variant update
    updateKlarnaPaymentValue(variant);

    // update clearance message and badge
    switchClearanceBadge(variant);

    // Replace the old sku and variant id with new ones
    replaceSkuAndVariantId(variant);

    if (firstTimeLoading) {
      const variantChange = new CustomEvent('variantChange');
      document.dispatchEvent(variantChange);
    }

    // Update the final sale message in PDP
    toggleFinalSaleMessage(variant, product_json);

    // Sync the sticky nav options
    syncStickyNavOptions(variant);

    // Sync secondary sticky nav options
    syncStickySecondaryNavOptions(variant);

    // Sync Sold Out or Unavailable status on mobile sticky
    syncSoldOutItemsMobileSticky();

    // Update Constructor Tracking data attributes
    cnstrcTracking.updateVariation(variant);
  }, 0);
};

// Initialize Klarna data on page load (exported for use in ProductOptions)
export const initKlarnaOnPageLoad = (variant) => {
  if (!variant) return;

  // Fetch initial Klarna data
  updateKlarnaPaymentValue(variant);
};

export default updateSelectedVariant;
